import * as React from "react";
import { useEffect, useState } from "react";
import { VscWarning } from "react-icons/vsc";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { theme } from "styles/theme";
import type { AssigneeConflict } from "./checkAppointmentConflicts";
import { ConflictDetailList } from "./appointmentConflictMessages";

/** Only show the loading copy if the check is still in flight after this long. */
const CHECKING_MESSAGE_DELAY_MS = 500;

type Props = {
  conflicts: AssigneeConflict[];
  isChecking?: boolean;
  timezone: string;
};

function useDelayedTrue(active: boolean, delayMs: number): boolean {
  const [delayed, setDelayed] = useState(false);

  useEffect(() => {
    if (!active) {
      setDelayed(false);
      return;
    }
    const handle = window.setTimeout(() => setDelayed(true), delayMs);
    return () => window.clearTimeout(handle);
  }, [active, delayMs]);

  return delayed;
}

export function AppointmentConflictWarning({
  conflicts,
  isChecking,
  timezone,
}: Props) {
  const { t } = useTranslation();
  const tBase = "containers.appointmentCard.appointment-folder.appointmentModal";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const showCheckingMessage = useDelayedTrue(
    Boolean(isChecking && conflicts.length === 0),
    CHECKING_MESSAGE_DELAY_MS
  );

  if (!conflicts.length && !showCheckingMessage) {
    return null;
  }

  return (
    <ConflictRow>
      <LabelSpacer aria-hidden="true" />
      <AlertBox>
        <AlertHeader>
          <VscWarning aria-hidden />
          <AlertTitle>{tr("Schedule conflicts")}</AlertTitle>
        </AlertHeader>
        {showCheckingMessage ? (
          <AlertBody>{tr("Checking assignee availability ellipsis")}</AlertBody>
        ) : (
          <ConflictDetailList
            conflicts={conflicts}
            timezone={timezone}
            maxVisible={3}
          />
        )}
        {conflicts.length > 0 ? (
          <AlertHint>{tr("Assignee conflict save hint")}</AlertHint>
        ) : null}
      </AlertBox>
    </ConflictRow>
  );
}

/** Matches `Row` in AppointmentModal (1fr label / 3fr field). */
const ConflictRow = styled.div`
  margin: 0.5rem 0;
  display: grid;
  align-items: start;
  grid-template-columns: 1fr 3fr;
  gap: 1rem;
`;

const LabelSpacer = styled.span`
  display: block;
`;

const AlertBox = styled.div`
  padding: 0.65rem 0.75rem;
  border: 1px solid #ca8a04;
  border-radius: 4px;
  background: #fffbeb;
  color: ${theme.color.subtext};
  font-size: ${theme.fontSize.regular};
  line-height: 1.45;
`;

const AlertHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  color: #a16207;

  svg {
    flex-shrink: 0;
    font-size: 1.15rem;
  }
`;

const AlertTitle = styled.span`
  font-weight: bold;
  font-size: ${theme.fontSize.small};
`;

const AlertBody = styled.p`
  margin: 0;
  font-size: ${theme.fontSize.small};
  color: ${theme.color.subtext2};
`;

const AlertHint = styled.p`
  margin: 0.5rem 0 0;
  font-size: ${theme.fontSize.xsmall};
  color: ${theme.color.subtext2};
`;
